import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useGame } from '../context/GameContext';
import RecipeViewer from './RecipeViewer';

const { FiArrowLeft, FiUser, FiTrendingUp, FiCoffee, FiStar, FiAward, FiTarget, FiEye } = FiIcons;

function ProfileScreen({ onNavigate }) {
  const { state, dispatch } = useGame();
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [showRecipeViewer, setShowRecipeViewer] = useState(false);

  const achievements = [
    {
      id: 'first_drink',
      name: 'First Brew',
      description: 'Serve your first drink',
      icon: FiCoffee,
      unlocked: state.stats.drinksServed >= 1,
      progress: Math.min(state.stats.drinksServed, 1),
      target: 1
    },
    {
      id: 'perfect_ten',
      name: 'Perfect Ten',
      description: 'Serve 10 perfect drinks',
      icon: FiStar,
      unlocked: state.stats.perfectOrders >= 10,
      progress: Math.min(state.stats.perfectOrders, 10),
      target: 10
    },
    {
      id: 'matcha_master',
      name: 'Matcha Master',
      description: 'Serve 5 perfect matcha drinks',
      icon: FiCoffee,
      unlocked: (state.stats.perfectMatchaOrders || 0) >= 5,
      progress: Math.min((state.stats.perfectMatchaOrders || 0), 5),
      target: 5,
      category: 'matcha'
    },
    {
      id: 'green_specialist',
      name: 'Green Tea Specialist',
      description: 'Serve 25 matcha drinks total',
      icon: FiTarget,
      unlocked: (state.stats.matchaDrinksServed || 0) >= 25,
      progress: Math.min((state.stats.matchaDrinksServed || 0), 25),
      target: 25,
      category: 'matcha'
    },
    {
      id: 'speed_demon',
      name: 'Speed Demon',
      description: 'Average serving time under 15 seconds',
      icon: FiTrendingUp,
      unlocked: state.stats.averageTime > 0 && state.stats.averageTime < 15,
      progress: state.stats.averageTime > 0 ? Math.max(0, 15 - state.stats.averageTime) : 0,
      target: 15
    },
    {
      id: 'big_earner',
      name: 'Big Earner',
      description: 'Earn $1000 total',
      icon: FiTarget,
      unlocked: state.stats.totalEarnings >= 1000,
      progress: Math.min(state.stats.totalEarnings, 1000),
      target: 1000
    }
  ];

  const levelProgress = (state.experience % 100) / 100;
  const nextLevelExp = 100 - (state.experience % 100);
  const regularRecipes = state.recipes.filter(recipe => !recipe.includes('matcha'));
  const matchaRecipes = state.recipes.filter(recipe => recipe.includes('matcha'));

  const handleRecipeClick = (recipe) => {
    setSelectedRecipe(recipe);
    setShowRecipeViewer(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <motion.button
          className="bg-white/80 backdrop-blur-sm p-3 rounded-xl shadow-lg"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => onNavigate('menu')}
        >
          <SafeIcon icon={FiArrowLeft} className="text-xl text-gray-600" />
        </motion.button>
        
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 flex items-center">
            <SafeIcon icon={FiUser} className="mr-2 text-amber-600" />
            Barista Profile
          </h1>
        </div>
        
        <div className="w-12"></div>
      </div>

      <div className="max-w-4xl mx-auto space-y-6">
        {/* Profile Overview */}
        <motion.div
          className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="text-center mb-6">
            <div className="w-24 h-24 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiUser} className="text-4xl text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800">Master Barista</h2>
            <p className="text-gray-600">Level {state.level}</p>
            {state.level >= 4 && (
              <span className="inline-block mt-2 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                🍃 Matcha Specialist
              </span>
            )}
          </div>

          {/* Level Progress */}
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Level Progress</span>
              <span>{nextLevelExp} XP to next level</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <motion.div
                className="bg-gradient-to-r from-amber-400 to-orange-500 h-3 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${levelProgress * 100}%` }}
                transition={{ duration: 0.8, delay: 0.2 }}
              />
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-gradient-to-r from-green-100 to-green-200 rounded-xl">
              <p className="text-2xl font-bold text-green-600">${state.coins}</p>
              <p className="text-sm text-green-700">Coins</p>
            </div>
            <div className="text-center p-4 bg-gradient-to-r from-purple-100 to-purple-200 rounded-xl">
              <p className="text-2xl font-bold text-purple-600">{state.score}</p>
              <p className="text-sm text-purple-700">Best Score</p>
            </div>
            <div className="text-center p-4 bg-gradient-to-r from-blue-100 to-blue-200 rounded-xl">
              <p className="text-2xl font-bold text-blue-600">{state.stats.drinksServed}</p>
              <p className="text-sm text-blue-700">Drinks Served</p>
            </div>
            <div className="text-center p-4 bg-gradient-to-r from-yellow-100 to-yellow-200 rounded-xl">
              <p className="text-2xl font-bold text-yellow-600">{state.stats.perfectOrders}</p>
              <p className="text-sm text-yellow-700">Perfect Orders</p>
            </div>
          </div>

          {/* Matcha Stats (if unlocked) */}
          {state.level >= 4 && (
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-gradient-to-r from-green-200 to-green-300 rounded-xl">
                <p className="text-xl font-bold text-green-700">{state.stats.matchaDrinksServed || 0}</p>
                <p className="text-sm text-green-800">Matcha Drinks</p>
              </div>
              <div className="text-center p-4 bg-gradient-to-r from-green-200 to-green-300 rounded-xl">
                <p className="text-xl font-bold text-green-700">{state.stats.perfectMatchaOrders || 0}</p>
                <p className="text-sm text-green-800">Perfect Matcha</p>
              </div>
            </div>
          )}
        </motion.div>

        {/* Unlocked Recipes */}
        <motion.div
          className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <SafeIcon icon={FiCoffee} className="mr-2 text-amber-600" />
            Mastered Recipes ({state.recipes.length})
          </h3>

          {/* Coffee Recipes */}
          {regularRecipes.length > 0 && (
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-700 mb-3">Coffee Recipes</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {regularRecipes.map((recipe, index) => (
                  <motion.button
                    key={recipe}
                    className="bg-gradient-to-r from-amber-100 to-orange-100 p-3 rounded-xl text-center hover:from-amber-200 hover:to-orange-200 transition-all group"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 + index * 0.05 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleRecipeClick(recipe)}
                  >
                    <span className="text-2xl mb-1 block">☕</span>
                    <p className="text-sm font-medium text-gray-800 capitalize mb-1">
                      {recipe.replace('_', ' ')}
                    </p>
                    <div className="flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <SafeIcon icon={FiEye} className="text-xs text-amber-600 mr-1" />
                      <span className="text-xs text-amber-600">View Recipe</span>
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>
          )}

          {/* Matcha Recipes */}
          {matchaRecipes.length > 0 && (
            <div>
              <h4 className="text-lg font-semibold text-gray-700 mb-3 flex items-center">
                🍃 Matcha Collection
                <span className="ml-2 bg-green-100 text-green-600 px-2 py-1 rounded-full text-xs">
                  Level 4+
                </span>
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {matchaRecipes.map((recipe, index) => (
                  <motion.button
                    key={recipe}
                    className="bg-gradient-to-r from-green-100 to-green-200 p-3 rounded-xl text-center border-2 border-green-300 hover:from-green-200 hover:to-green-300 transition-all group"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 + regularRecipes.length * 0.05 + index * 0.05 }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleRecipeClick(recipe)}
                  >
                    <span className="text-2xl mb-1 block">🍃</span>
                    <p className="text-sm font-medium text-gray-800 capitalize mb-1">
                      {recipe.replace('_', ' ')}
                    </p>
                    <div className="flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <SafeIcon icon={FiEye} className="text-xs text-green-600 mr-1" />
                      <span className="text-xs text-green-600">View Recipe</span>
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>
          )}
        </motion.div>

        {/* Achievements */}
        <motion.div
          className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <SafeIcon icon={FiAward} className="mr-2 text-amber-600" />
            Achievements
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {achievements.map((achievement, index) => (
              <motion.div
                key={achievement.id}
                className={`p-4 rounded-xl border-2 transition-all ${
                  achievement.unlocked
                    ? achievement.category === 'matcha'
                      ? 'bg-gradient-to-r from-green-50 to-green-100 border-green-300'
                      : 'bg-gradient-to-r from-green-50 to-green-100 border-green-300'
                    : 'bg-gray-50 border-gray-200'
                }`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
              >
                <div className="flex items-center mb-3">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
                      achievement.unlocked
                        ? achievement.category === 'matcha'
                          ? 'bg-green-500 text-white'
                          : 'bg-green-500 text-white'
                        : 'bg-gray-300 text-gray-600'
                    }`}
                  >
                    <SafeIcon icon={achievement.icon} className="text-lg" />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-800 flex items-center">
                      {achievement.name}
                      {achievement.category === 'matcha' && (
                        <span className="ml-2 text-green-600">🍃</span>
                      )}
                    </h4>
                    <p className="text-sm text-gray-600">{achievement.description}</p>
                  </div>
                </div>
                {!achievement.unlocked && (
                  <div>
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>Progress</span>
                      <span>{achievement.progress}/{achievement.target}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <motion.div
                        className={`h-2 rounded-full ${
                          achievement.category === 'matcha'
                            ? 'bg-gradient-to-r from-green-400 to-green-500'
                            : 'bg-gradient-to-r from-amber-400 to-orange-500'
                        }`}
                        initial={{ width: 0 }}
                        animate={{ width: `${(achievement.progress / achievement.target) * 100}%` }}
                        transition={{ duration: 0.8, delay: 0.5 + index * 0.1 }}
                      />
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Equipment Status */}
        <motion.div
          className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 shadow-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <SafeIcon icon={FiStar} className="mr-2 text-amber-600" />
            Equipment Levels
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(state.cafeUpgrades).map(([equipment, level]) => (
              <div
                key={equipment}
                className={`text-center p-4 rounded-xl ${
                  equipment === 'matchaWhisk'
                    ? 'bg-gradient-to-r from-green-100 to-green-200'
                    : 'bg-gradient-to-r from-blue-100 to-purple-100'
                }`}
              >
                <p
                  className={`text-lg font-bold ${
                    equipment === 'matchaWhisk' ? 'text-green-600' : 'text-purple-600'
                  }`}
                >
                  Level {level}
                </p>
                <p
                  className={`text-sm capitalize ${
                    equipment === 'matchaWhisk' ? 'text-green-700' : 'text-purple-700'
                  }`}
                >
                  {equipment.replace(/([A-Z])/g, ' $1').trim()}
                  {equipment === 'matchaWhisk' && ' 🍃'}
                </p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Reset Button */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <motion.button
            className="bg-gradient-to-r from-red-400 to-red-500 text-white px-6 py-3 rounded-2xl font-bold shadow-lg"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              if (confirm('Are you sure you want to reset all progress?')) {
                dispatch({ type: 'RESET_GAME' });
              }
            }}
          >
            Reset Progress
          </motion.button>
        </motion.div>
      </div>

      {/* Recipe Viewer Modal */}
      <RecipeViewer
        isOpen={showRecipeViewer}
        onClose={() => setShowRecipeViewer(false)}
        recipeId={selectedRecipe}
      />
    </div>
  );
}

export default ProfileScreen;